import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Calculator, Coins, Info, ExternalLink, ArrowRight } from "lucide-react";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ShareBar } from "@/components/site/ShareBar";
import { socialImageMeta } from "@/lib/social";
import { makeSeoDescription } from "@/lib/seo-description";
import { POINTS_SYSTEMS } from "@/lib/calculators/points";
import { TAX_SYSTEMS, computeTax } from "@/lib/calculators/tax";

const CANONICAL = "https://lifewestway.com/calculators";
const TITLE = "Migration & Tax Calculators 2026 | LifeWestWay";
const DESCRIPTION = makeSeoDescription(
  "Free skilled migration points and income tax calculators for the USA, UK, Australia, Canada, Italy and Portugal, built on official government criteria.",
);

export const Route = createFileRoute("/calculators")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: CalculatorsPage,
});

type Tab = "points" | "tax";

function CalculatorsPage() {
  const [tab, setTab] = useState<Tab>("points");
  const [pointsCode, setPointsCode] = useState("ca");
  const [taxCode, setTaxCode] = useState("gb");

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs items={[{ label: "Calculators" }]} />

      <header className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">Interactive tools</p>
        <h1 className="mt-3 font-display text-3xl font-black leading-tight text-foreground sm:text-4xl">
          Migration Points &amp; Tax Calculators for Six Western Countries
        </h1>
        <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-accent to-amber-400" />
        <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
          Two calculators, six destinations, one page. The skilled migration points calculator scores you against
          the published government criteria for Canada&apos;s Comprehensive Ranking System, Australia&apos;s
          General Skilled Migration points test and the UK&apos;s 70-point Skilled Worker test — and, for the three
          countries that run no points system at all, it walks you through the actual statutory conditions instead.
          The tax estimator applies the current national bracket tables to a salary you enter and shows the
          arithmetic band by band, so you can see exactly where every unit of currency goes.
        </p>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
          Nothing here is generated, sampled or guessed. Every rate, threshold and band is transcribed from the
          government source linked beneath each calculator, and the same inputs always produce the same output.
          Where a figure genuinely cannot be known from a salary alone — a US state income tax, an Italian
          municipal surcharge, a Canadian provincial rate — we leave it out and say so rather than inventing a
          number.
        </p>
      </header>

      {/* Tab switcher */}
      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setTab("points")}
          aria-pressed={tab === "points"}
          className={`group relative overflow-hidden rounded-2xl border p-6 text-left transition ${
            tab === "points"
              ? "border-accent/60 bg-accent/10 shadow-[0_20px_60px_-30px_rgba(212,175,55,0.8)]"
              : "border-border bg-card hover:border-accent/40"
          }`}
        >
          <Calculator className="h-6 w-6 text-accent" aria-hidden />
          <h2 className="mt-3 font-display text-xl font-bold text-foreground">Skilled Migration Points</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Score your profile against official immigration criteria for all six countries.
          </p>
        </button>
        <button
          type="button"
          onClick={() => setTab("tax")}
          aria-pressed={tab === "tax"}
          className={`group relative overflow-hidden rounded-2xl border p-6 text-left transition ${
            tab === "tax"
              ? "border-accent/60 bg-accent/10 shadow-[0_20px_60px_-30px_rgba(212,175,55,0.8)]"
              : "border-border bg-card hover:border-accent/40"
          }`}
        >
          <Coins className="h-6 w-6 text-accent" aria-hidden />
          <h2 className="mt-3 font-display text-xl font-bold text-foreground">Tax Estimator</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Estimate national income tax and take-home pay on the current bracket tables.
          </p>
        </button>
      </div>

      {tab === "points" ? (
        <PointsCalculator code={pointsCode} onCode={setPointsCode} />
      ) : (
        <TaxCalculator code={taxCode} onCode={setTaxCode} />
      )}

      <section className="mt-14">
        <h2 className="font-display text-2xl font-bold text-foreground">How these calculators work</h2>
        <div className="mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-accent to-amber-400" />
        <h3 className="mt-6 font-display text-lg font-semibold text-accent">Deterministic, not predictive</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Each calculator is a pure function of what you type. There is no model, no randomness and no stored
          data — your answers never leave your browser. Enter the same profile twice and you will get an identical
          result, which is precisely what makes the output checkable against the official tables we link to.
        </p>
        <h3 className="mt-6 font-display text-lg font-semibold text-accent">Where we deliberately stop</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Immigration decisions turn on documents, occupation lists and draw timing; tax bills turn on residence
          status, credits and local surcharges. A calculator that pretended to settle those questions would be
          misleading, so each tool lists what it excludes. Treat the numbers as a well-sourced starting point for a
          conversation with a registered migration agent or a qualified tax adviser, not as a substitute for one.
        </p>
        <h3 className="mt-6 font-display text-lg font-semibold text-accent">Kept current</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Thresholds change — usually in January for European countries, in April for the UK and in July for
          Australia. Each calculator shows the tax year or criteria set it is built on so you can tell at a glance
          whether it matches the year you are planning for, and every source link goes straight to the government
          page we transcribed.
        </p>
      </section>

      <div className="mt-10 rounded-xl border border-border bg-muted/30 p-5">
        <div className="flex gap-3">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
          <p className="text-sm leading-relaxed text-muted-foreground">
            <strong className="text-foreground">Disclaimer.</strong> These calculators are provided for general
            information and educational purposes only. They are estimates based on publicly published government
            criteria and do not constitute immigration, legal, financial or tax advice. Results are not a
            prediction of any visa outcome and are not a tax assessment. Always verify against the official source
            linked with each tool and seek advice from a qualified professional before acting.
          </p>
        </div>
      </div>

      <div className="mt-8">
        <ShareBar title="Migration & Tax Calculators" />
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/immigration"
          title="Read the immigration playbooks behind these points calculators"
          className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-accent/60"
        >
          Immigration playbooks <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/tax"
          title="Read the country tax guides behind this tax estimator"
          className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-accent/60"
        >
          Country tax guides <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function CountryPicker({
  items,
  code,
  onCode,
}: {
  items: { code: string; flag: string; country: string }[];
  code: string;
  onCode: (c: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((i) => (
        <button
          key={i.code}
          type="button"
          onClick={() => onCode(i.code)}
          aria-pressed={code === i.code}
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
            code === i.code
              ? "border-accent bg-accent/15 text-accent"
              : "border-border text-muted-foreground hover:border-accent/50 hover:text-foreground"
          }`}
        >
          <span aria-hidden>{i.flag}</span>
          {i.country}
        </button>
      ))}
    </div>
  );
}

function PointsCalculator({ code, onCode }: { code: string; onCode: (c: string) => void }) {
  const system = POINTS_SYSTEMS.find((s) => s.code === code) ?? POINTS_SYSTEMS[0];
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const current = values[system.code] ?? {};

  const result = useMemo(() => {
    const filled: Record<string, string> = {};
    for (const f of system.fields) filled[f.id] = current[f.id] ?? f.choices?.[0]?.value ?? "";
    return system.compute(filled);
  }, [system, current]);

  const set = (id: string, value: string) =>
    setValues((prev) => ({ ...prev, [system.code]: { ...(prev[system.code] ?? {}), [id]: value } }));

  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl font-bold text-foreground">Skilled Migration Points Calculator</h2>
      <div className="mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-accent to-amber-400" />
      <p className="mt-4 text-sm text-muted-foreground">Choose a destination to load its official criteria.</p>
      <div className="mt-4">
        <CountryPicker items={POINTS_SYSTEMS} code={system.code} onCode={onCode} />
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-lg font-bold text-accent">
          {system.flag} {system.name}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{system.summary}</p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {system.fields.map((f) => (
            <label key={f.id} className="block">
              <span className="block text-sm font-medium text-foreground">{f.label}</span>
              {f.help ? <span className="mt-0.5 block text-xs text-muted-foreground">{f.help}</span> : null}
              <select
                value={current[f.id] ?? f.choices?.[0]?.value ?? ""}
                onChange={(e) => set(f.id, e.target.value)}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none"
              >
                {f.choices?.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-accent/40 bg-accent/10 p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-display text-4xl font-black text-accent">{result.total}</span>
            <span className="text-sm font-semibold text-foreground">
              {system.kind === "points" ? `points out of ${result.max}` : `of ${result.max} criteria met`}
            </span>
            {result.pass ? (
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${
                  result.total >= result.pass ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"
                }`}
              >
                {result.total >= result.pass ? "Meets" : "Below"} the {result.pass}-point threshold
              </span>
            ) : null}
          </div>
          <p className="mt-3 text-sm leading-relaxed text-foreground">{result.verdict}</p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{system.passNote}</p>
        </div>

        <h4 className="mt-6 text-sm font-bold uppercase tracking-wider text-accent">Score breakdown</h4>
        <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
          {result.breakdown.map((b) => (
            <li key={b.label} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
              <span className="text-muted-foreground">
                {b.label}
                {b.note ? <span className="ml-2 text-xs text-muted-foreground/70">({b.note})</span> : null}
              </span>
              <span className="font-semibold text-foreground">{b.points}</span>
            </li>
          ))}
        </ul>

        <a
          href={system.source.href}
          target="_blank"
          rel="noopener"
          title={`Open the official source: ${system.source.label}`}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
        >
          Official source: {system.source.label} <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
}

function TaxCalculator({ code, onCode }: { code: string; onCode: (c: string) => void }) {
  const system = TAX_SYSTEMS.find((s) => s.code === code) ?? TAX_SYSTEMS[0];
  const [incomes, setIncomes] = useState<Record<string, string>>({});
  const raw = incomes[system.code] ?? "60000";
  const gross = Math.max(0, Number(raw) || 0);
  const result = useMemo(() => computeTax(system, gross), [system, gross]);

  const money = (n: number) =>
    `${system.symbol}${Math.round(n).toLocaleString("en-US")}`;

  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl font-bold text-foreground">Income Tax Estimator</h2>
      <div className="mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-accent to-amber-400" />
      <p className="mt-4 text-sm text-muted-foreground">Choose a country to load its current national bracket table.</p>
      <div className="mt-4">
        <CountryPicker items={TAX_SYSTEMS} code={system.code} onCode={onCode} />
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-lg font-bold text-accent">
          {system.flag} {system.name} · {system.taxYear}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{system.summary}</p>

        <label className="mt-6 block max-w-sm">
          <span className="block text-sm font-medium text-foreground">
            Gross annual income ({system.currency})
          </span>
          <input
            type="number"
            min={0}
            step={1000}
            inputMode="numeric"
            value={raw}
            onChange={(e) => setIncomes((p) => ({ ...p, [system.code]: e.target.value }))}
            className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2.5 text-lg font-semibold text-foreground focus:border-accent focus:outline-none"
          />
        </label>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <Stat label="Total tax &amp; levies" value={money(result.totalTax)} />
          <Stat label="Estimated net income" value={money(result.net)} highlight />
          <Stat label="Effective tax rate" value={`${(result.effectiveRate * 100).toFixed(1)}%`} />
        </div>

        <h4 className="mt-8 text-sm font-bold uppercase tracking-wider text-accent">Band-by-band breakdown</h4>
        <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
          <li className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
            <span className="text-muted-foreground">{system.allowanceLabel}</span>
            <span className="font-semibold text-foreground">−{money(result.allowance)}</span>
          </li>
          <li className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
            <span className="text-muted-foreground">Taxable income</span>
            <span className="font-semibold text-foreground">{money(result.taxable)}</span>
          </li>
          {result.rows.map((r, i) => (
            <li key={`${r.label}-${i}`} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
              <span className="text-muted-foreground">
                {r.label}
                {r.note ? <span className="ml-2 text-xs text-muted-foreground/70">{r.note}</span> : null}
              </span>
              <span className="font-semibold text-foreground">{money(r.amount)}</span>
            </li>
          ))}
          {result.extras.map((e) => (
            <li key={e.label} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
              <span className="text-muted-foreground">
                {e.label}
                <span className="ml-2 text-xs text-muted-foreground/70">{e.note}</span>
              </span>
              <span className="font-semibold text-foreground">{money(e.amount)}</span>
            </li>
          ))}
          <li className="flex items-center justify-between gap-4 bg-muted/40 px-4 py-3 text-sm">
            <span className="font-semibold text-foreground">Marginal rate on the next unit earned</span>
            <span className="font-bold text-accent">{(result.marginalRate * 100).toFixed(1)}%</span>
          </li>
        </ul>

        <h4 className="mt-6 text-sm font-bold uppercase tracking-wider text-accent">Not included in this estimate</h4>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {system.excludes.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>

        <a
          href={system.source.href}
          target="_blank"
          rel="noopener"
          title={`Open the official source: ${system.source.label}`}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
        >
          Official source: {system.source.label} <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-accent/50 bg-accent/10" : "border-border bg-background"}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-2xl font-black ${highlight ? "text-accent" : "text-foreground"}`}>{value}</p>
    </div>
  );
}
